// src/pages/BanDaoForm.js
import React, { useEffect, useState } from "react";
import TwoColumnLayout from "../layouts/TwoColumnLayout";
import { useHistory, useParams } from "react-router-dom"; // v5
import { apiFetch } from "../utils/wkCommon";
import { getUser } from "../utils/auth";
import PERMISSIONS, { fetchLunarForIntDate } from "../utils/wkCommon";
import {hasUmatPermissionAsync} from "../utils/permissionsAsync";
import AreaLookup from "../components/DropDownArea";
import { toPinyinSync, capitalizePinyin } from '../utils/pinyinUtil';

// --- Utilities ---
function toRem(px) {
  return `${px / 16}rem`;
}

// crude Chinese char detection
function containsChinese(text = "") {
  return /[\u3400-\u9FBF]/.test(text);
}

// naive pinyin placeholder — replace with proper library if desired
function generatePinyin(name = "") {
  // NOTE: this is a placeholder. For production, replace with a proper pinyin generator.
  // This simply returns ascii-only characters and spaces-to-dash.
  return name
    .normalize("NFKD")
    .replace(/[^\x00-\x7F]/g, "") // remove non-ascii (loses data for Chinese)
    .replace(/\s+/g, " ")
    .trim();
}

// generate UmatID: 2 uppercase letters + 2 digits
function genUmatID() {
  const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  const a = letters[Math.floor(Math.random() * letters.length)];
  const b = letters[Math.floor(Math.random() * letters.length)];
  const n = String(Math.floor(Math.random() * 100)).padStart(2, "0");
  return `${a}${b}${n}`;
}

// map gender rules given
function mapGender(g) {
  if (!g) return g;
  if (g == "男" || g === "童" || g === "乾") return "乾 Pria";
  if (g === "女" || g === "坤") return "坤 Wanita";
  return g;
}

// gender label helper
function genderLabel(value) {
  if (!value) return "";
  const map = {
    "乾": "乾 Pria",
    "坤": "坤 Wanita",
    "童": "童 Anak Laki",
    "女": "女 Anak Perempuan",
  };
  return map[value] ?? value;
}

// estimate birthdate (yyyy-mm-dd) from qiuDaoAgeText (assumed age integer)
// use bandaoDate (yyyy-mm-dd) as base; compute birthyear = bandaoYear - age
function estimateBirthdate(age, bandaoDate) {
  const year = bandaoDate ? new Date(bandaoDate).getFullYear() : new Date().getFullYear();
  if (!age || isNaN(Number(age))) return null;
  const byear = year - Number(age);
  return `${byear}-01-01`;
}

// --- Component ---
export default function BanDaoForm() {
    const history = useHistory();
  const { id } = useParams(); // optional: /bandao-form/:id

  const user = getUser();
  const currentUsername = user?.username ?? user?.LoginEmail ?? user?.email ?? "unknown";

  // BanDao fields
  // default to today (yyyy-mm-dd); when editing, effect akan menimpa dengan value dari record
  const today = new Date().toISOString().split("T")[0];
  const [IntDate, setIntDate] = useState(today); // yyyy-mm-dd
  const [fotangName, setFotangName] = useState("");
  const [Dcs, setDcs] = useState("");
  const [notes, setNotes] = useState("");
  const [suoshu_fotang, setSuoshu_fotang] = useState(null); // holds area id

  const [lunarInfo, setLunarInfo] = useState(null);

  // existing bandao (for edit)
  const [bandaoRecord, setBandaoRecord] = useState(null);
  const [loading, setLoading] = useState(false);

  // Qiu-Dao-Person entries to be created/edited in this form (local array)
  const [qiuEntries, setQiuEntries] = useState([]);
  const [editingIndex, setEditingIndex] = useState(null); // null = add new, number = edit existing index

  const hasUnsaved = qiuEntries.some(q => q._status === "NEW" || q._status === "MODIFIED");

  // temp form for adding a qiu person
  const [qiuTemp, setQiuTemp] = useState({
    qiuDaoName: "",
    qiuDaoAgeText: "",
    qiuDaoYear: "",
    qiuDaoDate: "",
    qiuDaoLocation: "",
    gender: "",
    yinShi: "",
    baoShi: "",
    address: "",
    phone: "",
    sd3Hari: false,
    qingKou: false,
    notes: "",
    gongDeFei: "",
  });

  const [canWriteQiuDaoRen, setCanWriteQiuDaoRen] = useState(false);
  const authUser = getUser();

  useEffect(() => {
    let mounted = true;
    async function loadPerms() {
      try {
        const email = authUser?.email;

        const a = await hasUmatPermissionAsync(email, PERMISSIONS.QIUDAOREN_WRITE);
        if (mounted) setCanWriteQiuDaoRen(Boolean(a));
      } catch (e) {
        console.warn("loadPerms failed", e);
      } finally {
        //if (mounted) setPermsReady(true);
      }
    }
    loadPerms();
    return () => { mounted = false; };
  }, [authUser]);

  useEffect(() => {
    if(!canWriteQiuDaoRen) return;
    // if editing, load bandao
    if (!id) return;
    setLoading(true);
    (async () => {
      try {
        const r = await apiFetch(`/api/bandaos/${id}?populate[suoshu_fotang]=true&populate[qiuDaoPeople][populate][umat]=true`);
        const data = r?.data || r;
        if (data) {
          const attrs = data;
          const loadedAreaId = attrs?.suoshu_fotang?.id ?? null;

          setBandaoRecord(data);
          setIntDate(attrs.IntDate ? attrs.IntDate.split("T")[0] : attrs.IntDate ?? "");
          setSuoshu_fotang(loadedAreaId);
          setFotangName(attrs.fotangName ?? "");
          setDcs(attrs.Dcs ?? "");
          setNotes(attrs.notes ?? "");

          // prefill qiuEntries from related qiu people if any
          const qlist = Array.isArray(attrs.qiuDaoPeople) ? attrs.qiuDaoPeople : [];
          setQiuEntries(Array.isArray(qlist)
            ? qlist.map(q => {
                const a = q;
                const umatId = a?.umat?.documentId ?? null;
                const hasUmat = !!umatId;
                                
                return { 
                  documentId: a.documentId ?? null,
                  umatId,
                  ...a,
                  _status: hasUmat ? "SYNCED" : "ERROR",
                  _errorType: hasUmat ? null : "UMAT_MISSING",
                };
              })
            : []
          );
        }
      } catch (e) {
        console.error("load bandao", e);
      } finally {
        setLoading(false);
      }
    })();
  }, [id, canWriteQiuDaoRen]);

  // fetch lunar whenever IntDate changes — use common util
  useEffect(() => {
    if (!IntDate) {
      setLunarInfo(null);
      return;
    }
    (async () => {
      try {
        const norm = String(IntDate).split("T")[0]; // -> "2025-11-27"
        const lunar = await fetchLunarForIntDate(norm);
        setLunarInfo(lunar);
      } catch (e) {
        console.error("fetch lunar", e);
        setLunarInfo(null);
      }
    })();
  }, [IntDate]);

  // handlers for qiu temp
  function onChangeQiuTemp(field, val) {
    setQiuTemp(prev => ({ ...prev, [field]: val }));
  }

  function buildComputedFieldsForQ(q) {
    // q: object possibly from qiuTemp
    const bandaoDate = IntDate ? String(IntDate).split("T")[0] : null; // "yyyy-mm-dd"
    const bandaoYear = bandaoDate ? new Date(bandaoDate).getFullYear() : new Date().getFullYear();
    const bandaoMonth = bandaoDate ? (new Date(bandaoDate).getMonth() + 1) : null;
    const bandaoDay = bandaoDate ? new Date(bandaoDate).getDate() : null;

    // qiuDaoYear: year from bandao.IntDate
    const qiuDaoYear = bandaoYear;

    // qiuDaoDate: the formatted string per requirement
    // example:
    // 歲次{lunar.lunarGanZhi} {lunar.leapMonth ? "閏" : ""}{lunar.lunarMonth}月{lunar.lunarDay}日 國曆 2020年2月9日 陳點傳師
    const lunar = lunarInfo ?? null;
    const lunarPart = lunar
      ? `歲次${lunar.lunarGanZhi ?? ""} ${lunar.leapMonth ? "閏" : ""}${lunar.lunarMonth ?? ""}月${lunar.lunarDay ?? ""}日`
      : `國曆 ${bandaoYear}年${bandaoMonth}月${bandaoDay}日`;

    const gregorianPart = `國曆 ${bandaoYear}年${bandaoMonth}月${bandaoDay}日`;
    const dcsPart = Dcs ? ` ${Dcs}` : "";

    const qiuDaoDate = `${lunarPart} ${gregorianPart}${dcsPart}`.trim();

    return {
      qiuDaoYear: String(qiuDaoYear),
      qiuDaoDate,
      sd3Hari: false,
      qingKou: false,
    };
  }

  function addQiuEntryLocal() {
    // validate all required (except notes)
    const q = qiuTemp || {};
    if (!q.qiuDaoName || !q.qiuDaoName.trim()) { alert("Nama QiuDaoRen harus diisi."); return; }
    if (!q.qiuDaoAgeText || !q.qiuDaoAgeText.toString().trim()) { alert("Usia harus diisi."); return; }
    if (!q.gender || !q.gender.trim()) { alert("Gender harus dipilih."); return; }
    if (!q.yinShi || !q.yinShi.trim()) { alert("YinShi harus diisi."); return; }
    if (!q.baoShi || !q.baoShi.trim()) { alert("BaoShi harus diisi."); return; }
    if (!q.address || !q.address.trim()) { alert("Alamat harus diisi."); return; }
    if (!q.phone || !q.phone.trim()) { alert("No HP/telepon harus diisi."); return; }
    if (!q.gongDeFei || !q.gongDeFei.trim()) { alert("Gong De Fei harus diisi."); return; }

    // compute automatic fields and merge into entry
    const computed = buildComputedFieldsForQ(q);
    const entry = {
      ...q,
      ...computed,
      _status: "NEW", // NEW | MODIFIED | SYNCED
    };

    if (editingIndex !== null && typeof editingIndex === "number") {
      // replace existing entry at editingIndex (preserve documentId if present)
      setQiuEntries(prev => {
        const copy = Array.isArray(prev) ? [...prev] : [];
        const prevItem = copy[editingIndex] ?? {};
        const documentId = prevItem?.documentId ?? entry?.documentId ?? null;
        copy[editingIndex] = { ...entry, documentId, _status: documentId ? "MODIFIED" : "NEW" };
        return copy;
      });
      setEditingIndex(null);
    } else {
      // add new
      setQiuEntries(prev => [...prev, entry]);
    }

    // reset temp
    setQiuTemp({
      qiuDaoName: "",
      qiuDaoAgeText: "",
      qiuDaoYear: "",
      qiuDaoDate: "",
      qiuDaoLocation: "",
      gender: "",
      yinShi: "",
      baoShi: "",
      address: "",
      phone: "",
      sd3Hari: false,
      qingKou: false,
      notes: "",
      gongDeFei: "", 
    });
  }

  function removeLocalQiu(idx) {
    const item = qiuEntries?.[idx];
    if (!item) return;
    if (item.documentId) {
      // sudah tersimpan di Strapi — tidak boleh dihapus dari form
      alert("Data ini sudah tersimpan di server dan tidak dapat dihapus dari form. Anda dapat mengedit datanya.");
      return;
    }
    // unsaved — boleh dihapus
    setQiuEntries(prev => prev.filter((_, i) => i !== idx));
    // if we were editing that index, cancel edit
    if (editingIndex === idx) setEditingIndex(null);
  }

  function editLocalQiu(idx) {
    const item = qiuEntries?.[idx];
    if (!item) return;
    // copy item into temp for editing
    // ensure we keep all fields expected by qiuTemp
    setQiuTemp({
      qiuDaoName: item.qiuDaoName ?? "",
      qiuDaoAgeText: item.qiuDaoAgeText ?? "",
      qiuDaoYear: item.qiuDaoYear ?? "",
      qiuDaoDate: item.qiuDaoDate ?? "",
      qiuDaoLocation: item.qiuDaoLocation ?? "",
      gender: item.gender ?? "",
      yinShi: item.yinShi ?? "",
      baoShi: item.baoShi ?? "",
      address: item.address ?? "",
      phone: item.phone ?? "",
      sd3Hari: !!item.sd3Hari,
      qingKou: !!item.qingKou,
      notes: item.notes ?? "",
      gongDeFei: item.gongDeFei ?? "",
    });
    setEditingIndex(idx);
    // optional: scroll to top of rightNode / focus first input
    const el = document.querySelector("#BanDaoForm");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  }

  // detect Han characters (Mandarin/Chinese)
  // Uses Unicode property escapes. If your env doesn't support it, replace with /[\u4E00-\u9FFF]/.
  const reHasHan = /\p{Script=Han}/u;
  const reOnlyHan = /^\p{Script=Han}+$/u;
  const reHanSegments = /\p{Script=Han}+/gu;

  // Main function
  function getPinyinName(inputName) {
    if (!inputName) return "";

    const name = String(inputName).trim();

    // If contains no Han characters — nothing to convert
    if (!reHasHan.test(name)) {
      // Option: if you want pinyin-ish for latin names, just capitalize
      return capitalizePinyin(name);
    }

    // If it's purely Han, convert whole string
    if (reOnlyHan.test(name)) {
      const py = toPinyinSync(name);            // your existing pinyin lib
      return capitalizePinyin(py);
    }

    // Mixed content: replace each Han-segment with its pinyin conversion
    // e.g. "王小明 (Willy)" => "Wang Xiaoming (Willy)" or "Wang Xiaoming Willy"
    let out = name;
    const segments = name.match(reHanSegments) || [];
    // Use Set to avoid repeated conversions for same segment
    const cache = new Map();
    segments.forEach(seg => {
      if (!cache.has(seg)) {
        const converted = toPinyinSync(seg); // should return space-separated pinyin words
        cache.set(seg, capitalizePinyin(converted));
      }
      out = out.replace(seg, cache.get(seg));
    });

    return out;
  }

  // --- form validation ---
  // all fields required except bandao:notes
  function validateForm() {
    // validate BanDao fields
    if (!IntDate) return { ok: false, msg: "Tanggal BanDao harus diisi." };
    if (!fotangName || !fotangName.trim()) return { ok: false, msg: "Nama Fotang harus diisi." };
    if (!Dcs || !Dcs.trim()) return { ok: false, msg: "Dianchuanshi harus diisi." };

    // qiuEntries must have at least one entry
    if (!Array.isArray(qiuEntries) || qiuEntries.length === 0) {
      return { ok: false, msg: "Harap tambahkan minimal satu Qiu-Dao-Person." };
    }

    // validate each qiu entry (all required except notes)
    for (let i = 0; i < qiuEntries.length; i++) {
      const q = qiuEntries[i] || {};
      const idx = i + 1;
      if (!q.qiuDaoName || !String(q.qiuDaoName).trim()) return { ok: false, msg: `Qiu #${idx}: Nama harus diisi.` };
      if (!q.qiuDaoAgeText || !String(q.qiuDaoAgeText).trim()) return { ok: false, msg: `Qiu #${idx}: Usia harus diisi.` };
      if (!q.gender || !String(q.gender).trim()) return { ok: false, msg: `Qiu #${idx}: Gender harus dipilih.` };
      if (!q.yinShi || !String(q.yinShi).trim()) return { ok: false, msg: `Qiu #${idx}: YinShi harus diisi.` };
      if (!q.baoShi || !String(q.baoShi).trim()) return { ok: false, msg: `Qiu #${idx}: BaoShi harus diisi.` };
      if (!q.address || !String(q.address).trim()) return { ok: false, msg: `Qiu #${idx}: Alamat harus diisi.` };
      if (!q.phone || !String(q.phone).trim()) return { ok: false, msg: `Qiu #${idx}: No HP/Telepon harus diisi.` };
      if (!q.gongDeFei || !String(q.gongDeFei).trim()) return { ok: false, msg: `Qiu #${idx}: Gong De Fei harus diisi.` };
      // sd3Hari and qingKou are booleans - they always have value so no check needed
    }

    return { ok: true };
  }

  function statusLabel(s, err) {
    if (s === "NEW") return "🟡 Baru (belum disimpan)";
    if (s === "MODIFIED") return "🟠 Diubah (belum sync)";
    if (s === "SYNCED") return "🟢 Tersimpan";

    if (s === "ERROR") {
      if (err === "UMAT_CREATE_FAILED") return "🔴 Gagal simpan record Umat (perlu resync)";
      if (err === "UMAT_MISSING") return "🔴 Umat belum ada (perlu resync)";
      if (err === "QIU_CREATE_FAILED") return "🔴 Gagal simpan QiuDaoRen (perlu resync)";
      if (err === "QIU_UPDATE_FAILED") return "🔴 Gagal update QiuDaoRen (perlu resync)";
      return "🔴 Error";
    }

    return "";
  }

  function umatPayloadFromQ(q) {
    return {
      domisiliSekitarJakarta: true,
      umatID: genUmatID(),
      displayName: q.qiuDaoName,
      fullName: !containsChinese(q.qiuDaoName) ? q.qiuDaoName : undefined,
      chineseName: containsChinese(q.qiuDaoName) ? q.qiuDaoName : undefined,
      pinyinName: containsChinese(q.qiuDaoName) ? getPinyinName(q.qiuDaoName) : undefined,
      birthdate: estimateBirthdate(q.qiuDaoAgeText, IntDate),
      area: suoshu_fotang ?? undefined,
      gender: mapGender(q.gender),
      mobilePhone: q.phone,
      address: q.address,
      guruPengajakYinShi: q.yinShi,
      guruPenjaminBaoShi: q.baoShi,
      fotangMemohonTao: fotangName || undefined,
      tanggalMemohonTao: IntDate || undefined,
      recordOwner: currentUsername,
      recordEditor: currentUsername,
    };
  }

  function buildQiuPayloadFromQ(q) {
    const bandaoDateOnly = IntDate ? String(IntDate).split("T")[0] : null;
    const bandaoYear = bandaoDateOnly ? new Date(bandaoDateOnly).getFullYear() : new Date().getFullYear();
    const bandaoMonth = bandaoDateOnly ? (new Date(bandaoDateOnly).getMonth() + 1) : null;
    const bandaoDay = bandaoDateOnly ? new Date(bandaoDateOnly).getDate() : null;

    const lunar = lunarInfo ?? null;

    const lunarPart = lunar
      ? `歲次${lunar.lunarGanZhi ?? ""} ${lunar.leapMonth ? "閏" : ""}${lunar.lunarMonth ?? ""}月${lunar.lunarDay ?? ""}日`
      : `國曆 ${bandaoYear}年${bandaoMonth}月${bandaoDay}日`;

    const gregorianPart = `國曆 ${bandaoYear}年${bandaoMonth}月${bandaoDay}日`;
    const dcsPart = Dcs ? ` ${Dcs}` : "";

    return {
      qiuDaoName: q.qiuDaoName,
      qiuDaoAgeText: q.qiuDaoAgeText,
      qiuDaoYear: String(bandaoYear),
      qiuDaoDate: `${lunarPart} ${gregorianPart}${dcsPart}`.trim(),
      qiuDaoLocation: fotangName,
      gender: q.gender,
      yinShi: q.yinShi,
      baoShi: q.baoShi,
      address: q.address,
      phone: q.phone,
      sd3Hari: false,
      qingKou: false,
      notes: q.notes,
      gongDeFei: q.gongDeFei,
      recordEditor: currentUsername,
    };
  }

  async function handleResync(q, idx) {
    if (!q || loading) return;

    setLoading(true);

    try {
      let umatId = q.umatId;
      let qiuId = q.documentId;

      // 🟥 CASE 1: UMAT gagal
      if (q._errorType === "UMAT_CREATE_FAILED" || q._errorType === "UMAT_MISSING") {
        const umatResp = await apiFetch("/api/umats", {
          method: "POST",
          body: { data: umatPayloadFromQ(q) }
        });
        umatId = umatResp?.data?.documentId;

        await apiFetch(`/api/qiu-dao-people/${qiuId}`, {
          method: "PUT",
          body: {
            data: { umat: umatId }
          }
        });
      }

      // 🟧 CASE 2: QIU create gagal
      if (q._errorType === "QIU_CREATE_FAILED") {
        const qiuResp = await apiFetch("/api/qiu-dao-people", {
          method: "POST",
          body: {
            data: {
              ...buildQiuPayloadFromQ(q),
              umat: umatId,
              bandao: id,
            }
          }
        });

        qiuId = qiuResp?.data?.documentId;
      }

      // 🟨 CASE 3: QIU update gagal
      if (q._errorType === "QIU_UPDATE_FAILED") {
        await apiFetch(`/api/qiu-dao-people/${qiuId}`, {
          method: "PUT",
          body: {
            data: buildQiuPayloadFromQ(q),
          }
        });
      }

      // ✅ update state
      setQiuEntries(prev => {
        const copy = [...prev];
        copy[idx] = {
          ...q,
          documentId: qiuId,
          umatId: umatId,
          _status: "SYNCED",
          _errorType: null,
        };
        return copy;
      });

    } catch (err) {
      console.error("RESYNC FAILED", err);
      alert("Resync gagal, cek console");
    } finally {
      setLoading(false);
    }
  }

  // --- Submission flow ---
  // Steps:
  // 1) if new BanDao -> create BanDao first (POST /api/bandaos) with recordOwner/Editor
  // 2) for each qiu entry:
  //    - create umat (POST /api/umats) with mapped fields and recordOwner/Editor
  //    - create qiu person (POST /api/qiu-dao-people) linking to the umat.documentId and bandao.documentId
  // 3) if edit mode (id present), update ban dao via PUT and create additional qiu similarly
  async function handleSubmit(e) {
    if (loading) return; 

    e.preventDefault();
    // client-side validation
    const v = validateForm();
    if (!v.ok) {
      alert(v.msg);
      return;
    }

    setLoading(true);
    try {
      let bandaoId = id ? id : null;
      // prepare bandao payload
      const bandaoPayload = {
        IntDate: IntDate || null,
        fotangName: fotangName || null,
        Dcs: Dcs || null,
        notes: notes || null,
        ...(suoshu_fotang ? { suoshu_fotang: suoshu_fotang } : {}),
        recordEditor: currentUsername,
      };
      if (!bandaoId) {
        // create
        const body = { data: { ...bandaoPayload, recordOwner: currentUsername } };
        const resp = await apiFetch("/api/bandaos", { method: "POST", body });

        const created = resp?.data ?? resp;
        bandaoId = created?.documentId ?? (created?.data?.documentId ?? null) ;
      } else {
        // update
        await apiFetch(`/api/bandaos/${bandaoId}`, { method: "PUT", body: { data: bandaoPayload } });
      }

      // after bandao create/update and bandaoId known
      const newEntries = [];

      for (const q of qiuEntries) {
        let umatId = null;
        let newQiuId = null;
        let errorType = null;

        const qiuPayloadBase = buildQiuPayloadFromQ(q);
        /*
        const qiuPayloadBase = {
          qiuDaoName: q.qiuDaoName,
          qiuDaoAgeText: q.qiuDaoAgeText,
          // override these with computed values (auto)
          qiuDaoYear: computedQiuDaoYear,
          qiuDaoDate: computedQiuDaoDate,
          qiuDaoLocation: fotangName,
          gender: q.gender,
          yinShi: q.yinShi,
          baoShi: q.baoShi,
          address: q.address,
          phone: q.phone,
          // auto booleans
          sd3Hari: false,
          qingKou: false,
          notes: q.notes,
          gongDeFei: q.gongDeFei,
          recordEditor: currentUsername,
        };
        */

        if (q.documentId) {
          // EXISTS -> update qiu-person (PUT), do NOT touch umat
          console.log("update qiu-person (PUT), do NOT touch umat", q.documentId)
          try {
            console.log("qiuPayloadBase",qiuPayloadBase)
            console.log("qiudaoren fetch url",`/api/qiu-dao-people/${q.documentId}`)
            await apiFetch(`/api/qiu-dao-people/${q.documentId}`, {
              method: "PUT",
              body: { data: { ...qiuPayloadBase } },
            });
          } catch (err) {
            console.error("update qiudao-person failed for id", q.documentId, err);
            errorType = "QIU_UPDATE_FAILED";
            // optionally: collect errors and show after loop
          }

          newEntries.push({
            ...q,
            documentId: q.documentId,   
            umatId: q.umatId ?? null,   
            _status: errorType ? "ERROR" : "SYNCED",
            _errorType: errorType,
          });
        } else {
          // NEW -> create umat first, then create qiu-person linking to umat + bandao
          console.log("create umat first, then create qiu-person linking to umat + bandao", q.qiuDaoName)
          try {
            const umatPayload = umatPayloadFromQ(q);            
            /*
            const umatPayload = {
              domisiliSekitarJakarta: true,
              umatID: genUmatID(),
              displayName: q.qiuDaoName,
              fullName: !containsChinese(q.qiuDaoName) ? q.qiuDaoName : undefined,
              chineseName: containsChinese(q.qiuDaoName) ? q.qiuDaoName : undefined,
              pinyinName: containsChinese(q.qiuDaoName) ? getPinyinName(q.qiuDaoName) : undefined, //generatePinyin(q.qiuDaoName),
              birthdate: estimateBirthdate(q.qiuDaoAgeText, IntDate),
              area: suoshu_fotang ?? undefined,
              gender: mapGender(q.gender),
              mobilePhone: q.phone,
              address: q.address,
              guruPengajakYinShi: q.yinShi,
              guruPenjaminBaoShi: q.baoShi,
              fotangMemohonTao: fotangName || undefined,
              tanggalMemohonTao: IntDate || undefined,
              recordOwner: currentUsername,
              recordEditor: currentUsername,
            };
            */

            try {
              const createdUmatResp = await apiFetch("/api/umats", { method: "POST", body: { data: umatPayload } });
              const createdUmat = createdUmatResp?.data ?? createdUmatResp;
              umatId = createdUmat?.documentId ?? createdUmat?.data?.documentId ?? null;

              console.log("Record Umat created", umatId)
            } catch (err) {
              console.error("UMAT CREATE FAILED", err);
              errorType = "UMAT_CREATE_FAILED";
            }

            // create qiu-person and link to umat & bandao
            const qiuPayload = {
              ...qiuPayloadBase,
              recordOwner: currentUsername,
              recordEditor: currentUsername,
            };
            if (umatId) qiuPayload.umat = umatId;
            if (bandaoId) qiuPayload.bandao = bandaoId;

            if (!errorType) {
              try {            
                const createdQiuResp = await apiFetch("/api/qiu-dao-people", {
                  method: "POST",
                  body: { data: qiuPayload }
                });

                newQiuId = createdQiuResp?.data?.documentId ?? null;
              } catch (err) {
                console.error("QIU CREATE FAILED", err);
                errorType = "QIU_CREATE_FAILED";
              }
            }

            // ✅ TAMBAHKAN INI
            newEntries.push({
              ...q,
              documentId: newQiuId,
              umatId,
              _status: errorType ? "ERROR" : "SYNCED",
              _errorType: errorType,
            });
            console.log("qiu-dao-person created", q.qiuDaoName)

            // mapping/add to Umat: if you need to set relations on umat, do it here (optional)
            // e.g. await apiFetch(`/api/umats/${umatId}`, { method: "PUT", body: { data: { /* relations */ } } });
          } catch (err) {
            console.error("create new qiudao-person flow failed", err);
          }
        }
      }
      setQiuEntries(newEntries);

      // done -> redirect to QiuDaoBook and pass selected bandao id so book selects it
      //history.push("/QiuDaoBook", { selectedBandaoId: bandaoId });
    } catch (err) {
      console.error("submit error", err);
      alert("Terjadi error saat menyimpan. Cek console.");
    } finally {
      setLoading(false);
    }
  }

  const saveInHeader = (
    <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
      { canWriteQiuDaoRen && (
        <>
        <button
          disabled={loading}
          type="button"
          onClick={() => {
            setBandaoRecord(null);
            setIntDate(today);
            setFotangName("");
            setDcs("");
            setNotes("");
            setSuoshu_fotang(null);
            setQiuEntries([]);
            setEditingIndex(null);
          }}
          style={{
            background: "#06b681",
            color: "#fff",
            border: "none",
            padding: "0.5rem 0.9rem",
            borderRadius: "8px",
            cursor: "pointer",
            fontWeight: 700,
            margin: "0 0.5rem"
          }}
        >
          + BanDao Baru
        </button>      

        <button
          disabled={loading}
          onClick={() => {
            const form = document.getElementById("BanDaoForm");
            if (form) {
              // HTMLFormElement.requestSubmit() modern API (falls back to submit)
              if (typeof form.requestSubmit === "function") form.requestSubmit();
              else form.submit();
            }
          }}
          title="Simpan"
          style={{
            background: "#0b5ed7",
            color: "#fff",
            border: "none",
            padding: "0.5rem 0.9rem",
            borderRadius: "8px",
            cursor: "pointer",
            fontWeight: 700,
          }}
        >
          {loading ? "Menyimpan..." : (id ? "Update" : "Simpan")}
        </button>
        </>
      )}
    </div>
  );

  function getMasterStatus() {
    if (!id) return { label: "🟡 New", aria: "new" };

    const hasDetail = Array.isArray(qiuEntries) && qiuEntries.length > 0;

    if (!hasDetail) {
      return {
        label: "🟠 Draft",
        aria: `bandao.${id}`
      };
    }

    return {
      label: "🟢 Update",
      aria: `bandao.${id}`
    };
  }

  return (
    <TwoColumnLayout
      headerActions={saveInHeader}
      title={id ? "BanDao - Perubahan Data" : "BanDao - Entry Baru"}
      leftNode={
        <div style={{ padding: toRem(12), fontSize: toRem(16) }}>
          <h3 style={{ fontSize: toRem(18), marginBottom: toRem(8) }}>辦道 Ban Dao</h3>
          {(() => {
            const s = getMasterStatus();
            return (
              <div
                style={{
                  fontSize: toRem(14),
                  fontWeight: 600,
                  marginBottom: toRem(8)
                }}
                aria-label={s.aria}
                title={s.aria}
              >
                Status: {s.label}
              </div>
            );
          })()}          
          <div style={{ color: "#6b7280", fontSize: toRem(14), paddingBottom: toRem(10) }}>
            Nama Fotang dan Dianchuanshi harap diketik dalam bahasa mandarin / hanzi 漢子. 
          </div>
          <form id="BanDaoForm" onSubmit={handleSubmit}>
            <div style={{ display: "flex", marginTop: toRem(12) }}>
              <label style={{ width: "50%", marginBottom: toRem(8), fontSize: toRem(16) }}>辦道日期 Tanggal</label>
              <div style={{ width: "50%", textAlign:"right", marginTop: toRem(0), fontSize: toRem(16) }}>
                {lunarInfo ? (
                  <div>
                    歲次{lunarInfo?.lunarGanZhi ?? ""} {lunarInfo?.leapMonth ? "閏" : ""}{lunarInfo?.lunarMonth ?? ""}月{lunarInfo?.lunarDay ?? ""}日
                  </div>
                ) : (
                  <div style={{ color: "#6b7280" }}>Tidak ada data lunar untuk tanggal ini</div>
                )}
              </div>
            </div>
            <input
              type="date"
              value={IntDate}
              onChange={(e) => setIntDate(e.target.value)}
              style={{ width: "95%", padding: toRem(8), fontSize: toRem(16), marginBottom: toRem(12) }}
              required
            />

            <label style={{ display: "block", marginBottom: toRem(8), fontSize: toRem(16) }}>所屬佛堂 Fotang Area</label>
            <AreaLookup value={suoshu_fotang} 
                onChange={(v) => setSuoshu_fotang(v)} 
                onSelectOption={(opt) => {
                  // kalau fotangName masih kosong/null, isi dengan area.shortname (fallback ke name)
                  if ((!fotangName || !fotangName.toString().trim()) && opt) {
                    const candidate = (opt.shortname && opt.shortname.toString().trim()) ? opt.shortname : opt.name;
                    if (candidate) setFotangName(candidate);
                  }
                }}
                placeholder="-- pilih Area --" 
            />

            <label style={{ display: "block", marginBottom: toRem(8), paddingTop: toRem(4), fontSize: toRem(16) }}>壇名 Nama Fotang</label>
            <input
              placeholder="請用中文輸入"
              value={fotangName}
              onChange={(e) => setFotangName(e.target.value)}
              style={{ width: "95%", padding: toRem(8), fontSize: toRem(16), marginBottom: toRem(12) }}
            />

            <label style={{ display: "block", marginBottom: toRem(8), fontSize: toRem(16) }}>點傳師 Dianchuanshi</label>
            <input placeholder="請用中文輸入" value={Dcs} onChange={(e) => setDcs(e.target.value)} style={{ width: "95%", padding: toRem(8), fontSize: toRem(16), marginBottom: toRem(12) }} />

            <label style={{ display: "block", marginBottom: toRem(8), fontSize: toRem(16) }}>備註 Catatan</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} style={{ width: "95%", padding: toRem(8), fontSize: toRem(16), marginBottom: toRem(12) }} />

            {/* submit button removed from form; headerActions will trigger submit */}
            {/*
            <div>
              <button type="submit" style={{ padding: `${toRem(8)} ${toRem(12)}`, fontSize: toRem(16) }}>
                {loading ? "Menyimpan..." : id ? "Update BanDao & Simpan Qiu-Dao" : "Simpan BanDao & Qiu-Dao"}
              </button>
            </div>
            */}
          </form>
        </div>
      }
      rightNode={
        <div style={{ padding: toRem(12), fontSize: toRem(16) }}>
          {hasUnsaved && (
            <span style={{ color: "orange", fontSize: toRem(14) }}>
              Ada perubahan belum disimpan
            </span>
          )}          
          <h3 style={{ fontSize: toRem(18), marginBottom: toRem(8) }}>QiuDaoRen - Data Entry</h3>
          <div style={{ color: "#6b7280", fontSize: toRem(14), paddingBottom: toRem(10) }}>Penambahan data Qiudaoren akan disimpan menjadi data Umat baru; Perubahan data Qiudaoren tidak merubah data Umat, ubah data Umat melalui menu lain. </div>

          {/* temp form */}
          <div style={{ marginBottom: toRem(8) }}>
            <input
              placeholder="中文名字 Nama Mandarin atau sesuai KTP"
              value={qiuTemp.qiuDaoName}
              onChange={(e) => onChangeQiuTemp("qiuDaoName", e.target.value)}
              style={{ width: "95%", padding: toRem(8), fontSize: toRem(16), marginBottom: toRem(8) }}
            />
            <div style={{ display: "flex", gap: toRem(8), marginBottom: toRem(8), paddingRight: toRem(10) }}>
              <input placeholder="年齡 Usia" value={qiuTemp.qiuDaoAgeText} onChange={(e) => onChangeQiuTemp("qiuDaoAgeText", e.target.value)} style={{ flex: 1, padding: toRem(8), fontSize: toRem(16) }} />
              <select
                value={qiuTemp.gender}
                onChange={(e) => onChangeQiuTemp("gender", e.target.value)}
                style={{ width: toRem(215), padding: toRem(8), fontSize: toRem(16) }}
              >
                <option value="">Pilih Gender</option>
                <option value="乾">乾 Pria</option>
                <option value="坤">坤 Wanita</option>
                <option value="童">童 Anak Laki</option>
                <option value="女">女 Anak Perempuan</option>
              </select>
            </div>

            <input placeholder="引師 Pengajak" value={qiuTemp.yinShi} onChange={(e) => onChangeQiuTemp("yinShi", e.target.value)} style={{ width: "95%", padding: toRem(8), fontSize: toRem(16), marginBottom: toRem(8) }} />
            <input placeholder="保師 Penjamin" value={qiuTemp.baoShi} onChange={(e) => onChangeQiuTemp("baoShi", e.target.value)} style={{ width: "95%", padding: toRem(8), fontSize: toRem(16), marginBottom: toRem(8) }} />
            <input placeholder="電話 No HP/Telepon" value={qiuTemp.phone} onChange={(e) => onChangeQiuTemp("phone", e.target.value)} style={{ width: "95%", padding: toRem(8), fontSize: toRem(16), marginBottom: toRem(8) }} />
            <input placeholder="住址 Alamat" value={qiuTemp.address} onChange={(e) => onChangeQiuTemp("address", e.target.value)} style={{ width: "95%", padding: toRem(8), fontSize: toRem(16), marginBottom: toRem(8) }} />
            <input placeholder="功德費 Gong De Fei" value={qiuTemp.gongDeFei} onChange={(e) => onChangeQiuTemp("gongDeFei", e.target.value)} style={{ width: "95%", padding: toRem(8), fontSize: toRem(16), marginBottom: toRem(8) }} />
            <textarea
              placeholder="備註 Catatan"
              value={qiuTemp.notes}
              onChange={(e) => onChangeQiuTemp("notes", e.target.value)}
              style={{ width: "95%", padding: toRem(8), fontSize: toRem(16), marginBottom: toRem(8) }}
            />

            <div style={{ display: "none",marginBottom: toRem(8) }}>
              <label style={{ marginRight: toRem(8) }}>
                <input type="checkbox" checked={qiuTemp.sd3Hari} onChange={(e) => onChangeQiuTemp("sd3Hari", e.target.checked)} /> sd3Hari
              </label>
              <label>
                <input type="checkbox" checked={qiuTemp.qingKou} onChange={(e) => onChangeQiuTemp("qingKou", e.target.checked)} /> qingKou
              </label>
            </div>

            <div style={{ display: "flex", gap: toRem(8) }}>
              { canWriteQiuDaoRen && (
                <button type="button" onClick={addQiuEntryLocal} style={{ padding: `${toRem(8)} ${toRem(10)}`, fontSize: toRem(16) }}>
                  { editingIndex !== null ? "Simpan Perubahan" : "Tambah Data QiuDaoRen" }
                </button>
              )}
            </div>
          </div>

          <hr style={{ margin: `${toRem(8)} 0` }} />

          <div>
            <h4 style={{ fontSize: toRem(16), marginBottom: toRem(8) }}>Daftar QiuDaoRen</h4>
            {qiuEntries.length === 0 && <div style={{ color: "#6b7280", fontSize: toRem(14) }}>Belum ada entry</div>}
            {qiuEntries.map((q, idx) => (
              <div key={idx} style={{ border: "1px solid #e5e7eb", padding: toRem(8), borderRadius: toRem(6), marginBottom: toRem(8) }}>
                <div style={{ fontWeight: 700, fontSize: toRem(15) }}>{q.qiuDaoName} &nbsp; {q.qiuDaoAgeText}歲 &nbsp; {q.gender ? `(${genderLabel(q.gender)})` : ""}</div>
                <div style={{ fontSize: toRem(14) }}>{q.phone} - {q.address}</div>
                <div style={{ fontSize: toRem(15) }}>引師: {q.yinShi} &nbsp;&nbsp; 保師: {q.baoShi} &nbsp;&nbsp; 功德費: {q.gongDeFei}</div>
                <div
                  style={{ fontSize: toRem(13), color: "#6b7280" }}
                  aria-label={`status:${q._status} | qiuId:${q.documentId || "-"} | umatId:${q.umatId || "-"}`}
                  title={`QiuID: ${q.documentId || "-"} | UmatID: ${q.umatId || "-"}`}
                >
                    {statusLabel(q._status, q._errorType)}
                </div>
                <div style={{ marginTop: toRem(6) }}>
                  <button type="button" onClick={() => editLocalQiu(idx)} style={{ padding: `${toRem(6)} ${toRem(8)}`, fontSize: toRem(14), marginRight: toRem(8) }}>
                    Edit
                  </button>

                  { !q.documentId ? (
                    <button type="button" onClick={() => removeLocalQiu(idx)} style={{ padding: `${toRem(6)} ${toRem(8)}`, fontSize: toRem(14) }}>
                      Hapus
                    </button>
                  ) : (
                    <button type="button" disabled title="Data sudah tersimpan di server; hanya bisa di-edit" style={{ padding: `${toRem(6)} ${toRem(8)}`, fontSize: toRem(14), opacity: 0.6 }}>
                      Hapus
                    </button>
                  )}

                  {q._status === "ERROR" && (
                    <button
                      onClick={() => handleResync(q, idx)}
                      disabled={loading}
                      style={{
                        marginTop: toRem(6),
                        padding: `${toRem(6)} ${toRem(8)}`,
                        fontSize: toRem(14),
                        background: "#f59e0b",
                        color: "#fff",
                        border: "none",
                        borderRadius: toRem(6),
                      }}
                    >
                      Resync Umat
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      }
    />
  );
}
